import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { CoreService } from '@barthauer/abc/queries/opath/core';
import { _AbcSplitterComponent, _AbcTreeNode } from '@barthauer/abc/ui-components';
import { ResizeEndEvent, ResizeStartEvent } from 'devextreme/ui/popup';
import { ResizeEvent } from 'devextreme/ui/resizable';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AbcQueriesEditorService } from './abc-queries-editor.service';
import { GeneralPropertiesComponent } from './core/properties/general-properties/general-properties.component';

import { CompareHelper } from '@barthauer/abc/core';
import { ValidationType } from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { QueryPropertiesComponent } from './core/properties/query-properties/query-properties.component';
import { QueriesTreeComponent } from './core/queries-tree/queries-tree.component';
import { ResultsComponent } from './core/results/results.component';
import { SelectColumnsComponent } from './core/select/select-columns/select-columns.component';
import { SelectObjectTypeComponent } from './core/select/select-object-type/select-object-type.component';
import { QueriesTreeService } from './services/queries-tree.service';
import { ResultsService } from './services/result.service';
import {
  ObjectSpaceInfo,
  PropertiesViews,
  Property,
  ResultsViews,
  SelectionViews,
  SideBarButtonId,
  SideBarPosition,
  SplitterConfig,
  TranslationConfiguration,
  UI_SETTINGS,
} from './viewModels/abc-queries-editor.viewModel';

@Component({
  selector: 'abc-opath-queries-editor-queries-editor',
  templateUrl: './abc-queries-editor.component.html',
  styleUrls: ['./abc-queries-editor.component.scss'],
  providers: [CoreService],
})
export class AbcQueriesEditorComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
  @ViewChild(QueriesTreeComponent) queriesTreeComponent: QueriesTreeComponent;
  @ViewChild(SelectObjectTypeComponent) selectObjectTypeComponent: SelectObjectTypeComponent;
  @ViewChild(SelectColumnsComponent) selectColumnsComponent: SelectColumnsComponent;
  @ViewChild(ResultsComponent) resultsComponent: ResultsComponent;
  @ViewChild(QueryPropertiesComponent) queryPropertiesComponent: QueryPropertiesComponent;
  @ViewChild(GeneralPropertiesComponent) generalPropertiesComponent: GeneralPropertiesComponent;

  @ViewChild('treeSplitterRef') treeSplitterComponent: _AbcSplitterComponent;
  @ViewChild('selectionSplitter') selectionSplitterComponent: _AbcSplitterComponent;
  @ViewChild('queryDesignerContainer') queryDesignerContainer: ElementRef<HTMLElement>;
  @ViewChild('content') queriesEditorContainer: ElementRef<HTMLElement>;
  @ViewChild('resultsBody') resultsBody: ElementRef<HTMLElement>;
  @ViewChild('layoutSplitterComponent') layoutSplitterComponent: _AbcSplitterComponent;

  private get layoutSplitterElement(): HTMLElement {
    return this.layoutSplitterComponent?.elemRef;
  }

  private $unsubscribe: Subject<void> = new Subject();
  private viewChecked: boolean = false;
  private resizeHandle: HTMLElement;
  private latestSelectedNodeId: String;
  private _rtlEnabled: boolean = true;
  private _currentSelectionView: SelectionViews;

  public resultsVisible: boolean = false;
  public PropertiesViews = PropertiesViews;
  public SelectionViews = SelectionViews;
  public realTimeQueryExec: boolean = false;
  public executeOnly: boolean = false;
  public currentResultsView: ResultsViews = ResultsViews.RESULTS_TABLE;
  public currentPropertyView: PropertiesViews;
  public splitterHandle: SideBarPosition = SideBarPosition.left;
  public resultsEmpty: boolean = false;

  @Input()
  set rtlEnabled(value: boolean) {
    this._rtlEnabled = value;
    this.splitterHandle = value ? SideBarPosition.left : SideBarPosition.right;
  }
  get rtlEnabled(): boolean {
    return this._rtlEnabled;
  }
  set currentSelectionView(view: SelectionViews) {
    if (!this._currentSelectionView && view) {
      this.handleTreeSplitterWidth();
      this.handleSelectionSplitterWidth();
    }
    this._currentSelectionView = view;
    this.handleTreeSlplitersMaxWidth();
    this.handleSelectionSplitterMaxWidth();
  }
  get currentSelectionView(): SelectionViews {
    return this._currentSelectionView;
  }

  public selectedNode: _AbcTreeNode<Property>;
  public queriesTreeData: _AbcTreeNode<Property>;
  public resAsync: Promise<ResourceSet>;
  public translation: TranslationConfiguration;
  public selectionSplitter: SplitterConfig = new SplitterConfig({
    minWidth: UI_SETTINGS.SELECTION_Min_WIDTH,
    maxWidth: UI_SETTINGS.SELECTION_Min_WIDTH,
  });
  public treeSplitter: SplitterConfig = new SplitterConfig({
    minWidth: UI_SETTINGS.TREE_Min_WIDTH,
    maxWidth: UI_SETTINGS.TREE_Min_WIDTH,
  });
  public uiSetting = UI_SETTINGS;
  private _treeSplitterWidth: number = UI_SETTINGS.TREE_Min_WIDTH;
  set treeSplitterWidth(value: number) {
    if (value >= UI_SETTINGS.TREE_Min_WIDTH) {
      this._treeSplitterWidth = value;
    }
  }
  get treeSplitterWidth(): number {
    return this._treeSplitterWidth;
  }
  private _selectionSplitterWidth: number = UI_SETTINGS.SELECTION_Min_WIDTH;
  set selectionSplitterWidth(value: number) {
    if (value >= UI_SETTINGS.SELECTION_Min_WIDTH) {
      this._selectionSplitterWidth = value;
    }
  }
  get selectionSplitterWidth(): number {
    return this._selectionSplitterWidth;
  }

  @Input() objectSpaceInfos: ObjectSpaceInfo[];
  @Input() queryDocumentId: string;
  @Input() parentResizeEvent: EventEmitter<ResizeStartEvent>;
  @Input() parentResizeEndEvent: EventEmitter<ResizeEndEvent>;
  @Input() parentMinHeight: number;

  constructor(
    private messageBoxService: MessageBoxService,
    private abcQueriesEditorService: AbcQueriesEditorService,
    private coreService: CoreService,
    private queriesTreeService: QueriesTreeService,
    private resultsService: ResultsService,
    private resourcesService: ResourceManagerService
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
    this.currentPropertyView = PropertiesViews.GENERAL_PROPERTIES;
  }

  async ngOnInit(): Promise<void> {
    const res: ResourceSet = await this.resAsync;

    this.translation = {
      menu: {
        save: res.get('menu-save'),
        saveAndClose: res.get('menu-saveAndClose'),
        executeAsTable: res.get('menu-executeAsTable'),
        executeAsJSON: res.get('menu-executeAsJSON'),
        executeAsXML: res.get('menu-executeAsXML'),
        showXMLQuery: res.get('menu-showXMLQuery'),
        showJSONQuery: res.get('menu-showJSONQuery'),
        resetTableLayout: res.get('menu-resetTableLayout'),
        reset: res.get('menu-reset'),
        executeAutomatically: res.get('menu-executeAutomatically'),
      },
      sideMenu: {
        insertQuery: res.get('sideMenu-insertQuery'),
        insertSubQuery: res.get('sideMenu-insertSubQuery'),
        insertColumns: res.get('sideMenu-insertColumns'),
        insertColumn: res.get('sideMenu-insertColumn'),
        duplicateColumn: res.get('sideMenu-duplicateColumn'),
        deleteColumn: res.get('sideMenu-deleteColumn'),
        upwards: res.get('sideMenu-upwards'),
        downwards: res.get('sideMenu-downwards'),
      },

      resultsTitle: res.get('queryEditor-resultsTitle'),
    };

    await this.loadControlButtons();
    this.handleTreeSlplitersMaxWidth();
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.queryDocumentId) {
      this.abcQueriesEditorService.queryDocumentId = this.queryDocumentId;
    }
    await this.initializeObjectSpaceInfos();

    this.queriesTreeComponent.nodeClickEvent
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(async (node: _AbcTreeNode<Property>) => {
        this.selectedNode = node;
        if (node.data) {
          if (node.data.columnProperties) {
            this.currentPropertyView = PropertiesViews.COLUMN_PROPERTIES;
            this.currentSelectionView = null;
          } else if (node.data.queryProperties) {
            this.currentPropertyView = PropertiesViews.QUERY_PROPERTIES;

            if (!node.children.length) {
              this.currentSelectionView = SelectionViews.SELECT_COLUMNS;
            } else {
              if (node.id !== this.latestSelectedNodeId) {
                this.currentSelectionView = null;
              } else {
                if (this.currentSelectionView !== SelectionViews.SELECT_COLUMNS) {
                  this.currentSelectionView = null;
                }
              }
            }

            this.latestSelectedNodeId = node.id;
          }
        } else {
          this.currentPropertyView = PropertiesViews.GENERAL_PROPERTIES;

          if (!node.children.length) {
            this.currentSelectionView = SelectionViews.SELECT_OBJECT_TYPE;
          } else {
            if (this.currentSelectionView !== SelectionViews.SELECT_OBJECT_TYPE) {
              this.currentSelectionView = null;
            }
          }
        }
        await this.loadControlButtons();
      });

    this.queriesTreeComponent.queriesTree.itemsChange
      .pipe(debounceTime(500), takeUntil(this.$unsubscribe))
      .subscribe(async (data) => {
        this.abcQueriesEditorService.queries = data[0] as _AbcTreeNode<Property>;
        this.queriesTreeData = data[0] as _AbcTreeNode<Property>;
        if (!this.queriesTreeData?.children.length) {
          this.closeResults();
          this.resultsEmpty = true;
        }
        this.resultsComponent?.refreshQueriesData();
        this.queryPropertiesComponent?.refreshSelectedNode(this.selectedNode);
        await this.loadControlButtons();
      });

    this.parentResizeEvent.pipe(takeUntil(this.$unsubscribe)).subscribe((event) => {
      this.resultsComponent?.refreshNestedGrid();
      this.updateResultsHeight(event.height, true);

      this.handleTreeSlplitersMaxWidth();
      if (this.currentSelectionView) {
        this.handleSelectionSplitterMaxWidth();
      }
    });

    this.parentResizeEndEvent?.pipe(takeUntil(this.$unsubscribe)).subscribe((event) => {
      this.handleTreeSlplitersMaxWidth();
      if (this.currentSelectionView) {
        this.handleSelectionSplitterMaxWidth();
      }
    });

    if (this.executeOnly) {
      this.toggleResults(ResultsViews.RESULTS_TABLE);
      setTimeout(() => {
        this.resultsComponent?.refreshQueriesData(true);
      }, 500);
    }
  }

  ngAfterViewChecked(): void {
    this.initializeUi();
  }

  ngOnDestroy(): void {
    this.currentPropertyView = PropertiesViews.GENERAL_PROPERTIES;
    this.currentSelectionView = null;

    this.resultsVisible = false;

    this.$unsubscribe.next();
    this.$unsubscribe.complete();
    this.queriesTreeService.expressionEditorInsertion.next([]);
    this.resultsService.resultTableCache = null;
    this.resultsService.resultXmlCache = '';
    this.resultsService.resultJsonCache = '';
  }

  public selectColumnsInsertion = (columns: _AbcTreeNode<Property>[]): void => {
    this.queriesTreeComponent.insertNodes(columns);
  };

  public refreshTree = (): void => {
    this.queriesTreeComponent.refreshTree();
  };

  public refreshSelectColumns = (node: _AbcTreeNode<Property>): void => {
    this.selectColumnsComponent && (this.selectColumnsComponent.selectedNode = node);
  };

  public refreshRoot(): void {
    if (this.selectObjectTypeComponent) {
      this.selectObjectTypeComponent.getObjectTypeInfos();
    }
    this.queriesTreeComponent.refreshRoot();
  }

  public reset = (): void => {
    if (this.queriesTreeComponent && this.queriesTreeComponent.queries.children.length > 0) {
      const settings = new MessageSettings();
      settings.mode = MessageMode.Warning;
      settings.icon = MessageBoxIcon.Question;
      settings.button = MessageBoxButtons.YesNo;
      settings.message = this.translation.menu.reset;
      this.messageBoxService.showMessageBox(settings).subscribe((result: DialogResult) => {
        if (DialogResult.Yes === result['result']) {
          this.emptyQueries();
          this.loadControlButtons();

          return;
        }
      });
    }
  };

  public emptyQueries = (): void => {
    if (this.queriesTreeComponent && this.queriesTreeComponent.queries.children.length > 0) {
      this.queriesTreeComponent.queriesTree.instance.beginUpdate();
      this.queriesTreeComponent.queriesTree.items[0]['caption'] = this.queriesTreeComponent.queriesTree.items[0].id;
      this.abcQueriesEditorService.rootElementName = '';
      if (this.generalPropertiesComponent) {
        this.generalPropertiesComponent.textBox.value = '';
      }
      const dataSource = this.queriesTreeComponent.queriesTree.instance.getDataSource();
      this.queriesTreeComponent.queries.children = [];
      this.queriesTreeComponent.queriesTree.dataSource = dataSource;
      this.queriesTreeComponent.queriesTree.instance.endUpdate();
      this.queriesTreeComponent.queriesTree.instance._refresh();

      this.currentPropertyView = PropertiesViews.GENERAL_PROPERTIES;
      this.currentSelectionView = SelectionViews.SELECT_OBJECT_TYPE;
    }
  };

  public deleteQueryContent = async (): Promise<void> => {
    return new Promise((resolve) => {
      this.queriesTreeComponent.queriesTree.instance.beginUpdate();
      const dataSource = this.queriesTreeComponent.queriesTree.instance.getDataSource();
      this.selectedNode.children = [];
      this.queriesTreeComponent.queriesTree.dataSource = dataSource;
      this.queriesTreeComponent.queriesTree.instance.endUpdate();
      this.queriesTreeComponent.queriesTree.instance._refresh();
      resolve();
    });
  };

  private async initializeObjectSpaceInfos(): Promise<void> {
    this.abcQueriesEditorService.objectSpaceInfos = this.objectSpaceInfos;
    this.abcQueriesEditorService.objectSpaceName =
      this.generalPropertiesComponent.objectSpaceName ??
      (this.objectSpaceInfos?.length ? this.objectSpaceInfos[0].value : '');
    this.abcQueriesEditorService.rootElementName = this.generalPropertiesComponent.rootElementName;
    this.queriesTreeComponent.objectSpaceName = this.abcQueriesEditorService.objectSpaceName;
    this.queriesTreeComponent.refreshRoot();
  }

  private async loadControlButtons(): Promise<void> {
    // Layout Top Control Buttons
    this.coreService.topControlButtons = [
      {
        icon: 'save',
        text: this.translation?.menu.save,
        visible:
          !this.executeOnly &&
          this.abcQueriesEditorService.queryDocumentId &&
          this.queriesTreeData?.children.length > 0,
        action: async () => {
          this.coreService.loaderText = (await this.resAsync).get('queryEditor-savingQuery');
          this.coreService.showLoader = true;
          await this.abcQueriesEditorService.saveQueryToDocument();
          this.coreService.showLoader = false;
        },
      },
      {
        icon: 'deletetable',
        text: this.translation?.menu.saveAndClose,
        visible:
          !this.executeOnly &&
          this.abcQueriesEditorService.queryDocumentId &&
          this.queriesTreeData?.children.length > 0,
        active: false,
        action: async () => {
          this.coreService.loaderText = (await this.resAsync).get('queryEditor-savingQuery');
          this.coreService.showLoader = true;
          await this.abcQueriesEditorService.saveQueryToDocument();
          this.coreService.showLoader = false;
          await this.abcQueriesEditorService.closeOpathQueriesEditor();
        },
      },
      {
        icon: 'fieldchooser',
        text: this.translation?.menu.executeAsTable,
        visible: true,
        active: false,
        action: async () => {
          this.toggleResults(ResultsViews.RESULTS_TABLE);
          setTimeout(() => {
            this.resultsComponent?.refreshQueriesData(true);
          }, 0);
        },
      },
      {
        icon: 'alignjustify',
        text: this.translation?.menu.executeAsJSON,
        visible: true,
        active: false,
        action: async () => {
          this.toggleResults(ResultsViews.RESULTS_JSON);
          setTimeout(() => {
            this.resultsComponent?.refreshQueriesData(true);
          }, 0);
        },
      },
      {
        icon: 'variable',
        text: this.translation?.menu.executeAsXML,
        visible: true,
        active: false,
        action: async () => {
          this.toggleResults(ResultsViews.RESULTS_XML);
          setTimeout(() => {
            this.resultsComponent?.refreshQueriesData(true);
          }, 0);
        },
      },
      {
        icon: 'exportxlsx',
        text: this.translation?.menu.showXMLQuery,
        visible: true,
        active: false,
        action: async () => this.toggleResults(ResultsViews.RESULTS_QUERY_XML),
      },
      {
        icon: 'textdocument',
        text: this.translation?.menu.showJSONQuery,
        visible: true,
        active: false,
        action: async () => this.toggleResults(ResultsViews.RESULTS_QUERY_JSON),
      },
      {
        icon: 'undo',
        text: this.translation?.menu.resetTableLayout,
        visible: this.queriesTreeData?.children.length > 0,
        active: false,
        action: async () => console.log('reset the table layout'),
      },
      {
        icon: 'refresh',
        text: this.translation?.menu.reset,
        visible: !this.executeOnly && this.queriesTreeData?.children.length > 0,
        active: false,
        action: async () => this.reset(),
      },
      {
        icon: 'runner',
        text: this.translation?.menu.executeAutomatically,
        visible: !this.executeOnly,
        active: this.realTimeQueryExec,
        action: async () => this.toggleAutoQueryExec(),
      },
    ];

    // Layout Side Control Buttons
    this.coreService.sideControlButtons = [
      {
        icon: 'doc',
        text: this.translation?.sideMenu.insertQuery,
        visible: !this.executeOnly,
        active: this.currentSelectionView === SelectionViews.SELECT_OBJECT_TYPE,
        action: async () => {
          if (this.currentSelectionView === SelectionViews.SELECT_OBJECT_TYPE) {
            this.currentSelectionView = null;
          } else {
            this.currentPropertyView = PropertiesViews.GENERAL_PROPERTIES;
            this.currentSelectionView = SelectionViews.SELECT_OBJECT_TYPE;
            this.loadControlButtons();
          }
        },
      },
      {
        icon: 'import',
        text: this.translation?.sideMenu.insertSubQuery,
        visible: !this.executeOnly && this.currentPropertyView === PropertiesViews.QUERY_PROPERTIES,
        active: false,
        action: async () => {
          await this.queriesTreeService.openOpathExpression(
            ValidationType.Query,
            this.selectedNode as _AbcTreeNode<Property>,
            this.abcQueriesEditorService.queryDocument?.ParameterDefinitions || [],
            SideBarButtonId.INSERT_SUB_QUERY,
            this.translation.sideMenu.insertSubQuery
          );
        },
      },
      {
        icon: 'inserttable',
        text: this.translation?.sideMenu.insertColumns,
        visible: !this.executeOnly && this.currentPropertyView === PropertiesViews.QUERY_PROPERTIES,
        active: this.currentSelectionView === SelectionViews.SELECT_COLUMNS,
        action: async () => {
          if (this.currentSelectionView == SelectionViews.SELECT_COLUMNS) {
            this.currentSelectionView = null;
          } else {
            this.currentPropertyView = PropertiesViews.QUERY_PROPERTIES;
            this.currentSelectionView = SelectionViews.SELECT_COLUMNS;
          }
          this.loadControlButtons();
        },
      },
      {
        icon: 'insertcolumnright',
        text: this.translation?.sideMenu.insertColumn,
        visible: !this.executeOnly && this.currentPropertyView === PropertiesViews.QUERY_PROPERTIES,
        active: false,
        action: async () => {
          await this.queriesTreeService.openOpathExpression(
            ValidationType.Column,
            this.selectedNode as _AbcTreeNode<Property>,
            this.abcQueriesEditorService.queryDocument?.ParameterDefinitions || [],
            SideBarButtonId.INSERT_COLUMN,
            this.translation.sideMenu.insertColumn
          );
        },
      },
      {
        icon: 'unselectall',
        text: this.translation?.sideMenu.duplicateColumn,
        visible: !this.executeOnly && this.currentPropertyView === PropertiesViews.COLUMN_PROPERTIES,
        active: false,
        action: async () => this.queriesTreeComponent.duplicateCurrentNode(),
      },
      {
        icon: 'trash',
        text: this.translation?.sideMenu.deleteColumn,
        visible: !this.executeOnly && this.currentPropertyView !== PropertiesViews.GENERAL_PROPERTIES,
        active: false,
        action: async () => {
          this.queriesTreeComponent.deleteCurrentNode();
        },
      },
      {
        icon: 'arrowup',
        text: this.translation?.sideMenu.upwards,
        visible: !this.executeOnly && this.currentPropertyView !== PropertiesViews.GENERAL_PROPERTIES,
        active: false,
        action: async () => this.queriesTreeComponent.upwardNode(),
      },
      {
        icon: 'arrowdown',
        text: this.translation?.sideMenu.downwards,
        visible: !this.executeOnly && this.currentPropertyView !== PropertiesViews.GENERAL_PROPERTIES,
        active: false,
        action: async () => this.queriesTreeComponent.downwardNode(),
      },
    ];
  }

  private initializeUi(): void {
    if (!this.viewChecked) {
      this.resizeHandle = this.layoutSplitterElement?.querySelector('.dx-resizable-handle-bottom');

      if (this.queryDesignerContainer?.nativeElement && this.resultsBody) {
        this.queryDesignerContainer.nativeElement.style.setProperty('height', 'calc(100% - 30px)');

        if (this.executeOnly) {
          setTimeout(() => {
            this.toggleResults(ResultsViews.RESULTS_TABLE);
          }, 100);
        } else {
          this.resultsBody?.nativeElement?.style.setProperty('height', '0px');
        }
      }

      if (this.resizeHandle) {
        this.resizeHandle.style.setProperty('pointer-events', 'none');

        this.viewChecked = true;
      }
    }
  }

  private toggleAutoQueryExec(): void {
    this.realTimeQueryExec = !this.realTimeQueryExec;
    this.loadControlButtons();
  }
  public dividerPanMove(e: ResizeEvent) {
    let parentContainerHeight = this.queryDesignerContainer?.nativeElement?.parentElement.parentElement.clientHeight;
    this.resultsComponent?.refreshNestedGrid();
    if (e.height < 500 && !this.executeOnly) {
      this.resultsBody?.nativeElement?.style.setProperty('height', parentContainerHeight - 30 - e.height + 'px');
    }
  }

  public toggleResults(view?: ResultsViews): void {
    if (view) {
      this.resultsVisible = true;
      this.currentResultsView = view;
    } else {
      this.resultsVisible = !this.resultsVisible;
      this.currentResultsView = ResultsViews.RESULTS_TABLE;
    }
    this.updateResultsHeight(280);
  }

  private updateResultsHeight(visibleHeight: number, isResize?: boolean): void {
    let newHeight: string;
    if (this.resultsVisible) {
      const parentContainerHeight =
        this.queryDesignerContainer?.nativeElement?.parentElement.parentElement.clientHeight;
      if (!CompareHelper.isNullorEmpty(newHeight) && this.layoutSplitterElement) {
        if (this.executeOnly) {
          newHeight = visibleHeight - 73 + 'px';
        } else {
          newHeight = visibleHeight - this.layoutSplitterElement.clientHeight - 68 + 'px';
        }
      } else if (this.executeOnly) {
        this.layoutSplitterElement?.style.setProperty('height', this.uiSetting.HEADER_HEIGHT + 'px');
        newHeight = parentContainerHeight - this.uiSetting.HEADER_HEIGHT + 'px';
      } else if (isResize) {
        if (visibleHeight > this.parentMinHeight) {
          newHeight = visibleHeight - this.layoutSplitterElement.clientHeight - 68 + 'px';
        }
      } else {
        this.layoutSplitterElement?.style.setProperty('height', visibleHeight + 'px');
        newHeight = parentContainerHeight - (visibleHeight + 30) + 'px';
        this.resizeHandle.style.setProperty('pointer-events', 'all');
      }
    } else {
      newHeight = '0px';
      this.layoutSplitterElement?.style.setProperty('height', '100%');
      this.resizeHandle?.style.setProperty('pointer-events', 'none');
      this.queryPropertiesComponent?.grid?.instance?.refresh();
    }
    this.resultsBody?.nativeElement?.style.setProperty('height', newHeight);
  }

  public closeResults(): void {
    this.resultsVisible = false;
    this.currentResultsView = ResultsViews.RESULTS_TABLE;
    this.layoutSplitterElement?.style.setProperty('height', '100%');
    this.resultsBody?.nativeElement?.style.setProperty('height', '0px');
    this.resizeHandle.style.setProperty('pointer-events', 'none');
  }

  public closeSelectionView = (): void => {
    this.currentSelectionView = null;
  };

  private handleTreeSplitterWidth(): void {
    if (
      this.queriesEditorContainer.nativeElement.clientWidth - this.treeSplitterWidth - this.selectionSplitter.minWidth <
      UI_SETTINGS.PROPERTIES_Min_WIDTH
    ) {
      this.treeSplitterWidth -= this.selectionSplitter.minWidth;
    }
  }

  private handleSelectionSplitterWidth(): void {
    if (
      this.queriesEditorContainer.nativeElement.clientWidth -
        this.treeSplitterWidth -
        UI_SETTINGS.PROPERTIES_Min_WIDTH <
      this.selectionSplitterWidth
    ) {
      this.selectionSplitterWidth =
        this.queriesEditorContainer.nativeElement.clientWidth -
        this.treeSplitterWidth -
        UI_SETTINGS.PROPERTIES_Min_WIDTH;
    }
  }

  public handleSelectionSplitterMaxWidth(): void {
    const querieEditorWidth = this.queriesEditorContainer.nativeElement.clientWidth;
    this.selectionSplitter.maxWidth = querieEditorWidth - this.treeSplitterWidth - UI_SETTINGS.PROPERTIES_Min_WIDTH;
  }

  public handleTreeSlplitersMaxWidth(view?: SelectionViews): void {
    const querieEditorWidth = this.queriesEditorContainer.nativeElement.clientWidth;
    if (view ? view : this.currentSelectionView) {
      this.treeSplitter.maxWidth = querieEditorWidth - UI_SETTINGS.PROPERTIES_Min_WIDTH - this.selectionSplitterWidth;
    } else {
      this.treeSplitter.maxWidth = querieEditorWidth - UI_SETTINGS.PROPERTIES_Min_WIDTH;
    }

    if ((this.treeSplitterComponent.width as number) < this.treeSplitterWidth) {
      this.treeSplitterComponent.width = this.treeSplitterWidth;
    }
  }
}
